import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { AiOutlineLoading } from "react-icons/ai";
import { Link } from "react-router-dom";
import { Breadcrumb, NavBar, SideNav } from "../../components";
import { timestampToDateString } from "../../config/firebase/index";
import { Button, Avatar } from "flowbite-react";
import {
  fetchActiveApplications,
  fetchInactiveApplications,
  updateApplicationStatus,
} from "../../redux/slices/applications";

const boardKeys = {
  awaitingPayment: "awaiting_confirmation",
  readyForDevelopment: "in_the_queue",
  inProgress: "in_progress",
  done: "done",
  blocked: "awaiting_customer",
};
const PAGE_LIMIT = 10;

const initialData = {
  tasks: {},
  columns: {
    [boardKeys.awaitingPayment]: {
      id: boardKeys.awaitingPayment,
      title: "Awaiting Payment",
      taskIds: [],
    },
    [boardKeys.readyForDevelopment]: {
      id: boardKeys.readyForDevelopment,
      title: "Ready for Pickup",
      taskIds: [],
    },
    [boardKeys.inProgress]: {
      id: boardKeys.inProgress,
      title: "In Progress",
      taskIds: [],
    },
    [boardKeys.blocked]: {
      id: boardKeys.blocked,
      title: "Awaiting Customer",
      taskIds: [],
    },
    [boardKeys.done]: { id: boardKeys.done, title: "Done", taskIds: [] },
  },
  columnOrder: [
    boardKeys.awaitingPayment,
    boardKeys.readyForDevelopment,
    boardKeys.inProgress,
    boardKeys.blocked,
    boardKeys.done,
  ],
};

const transformApplicationsToBoardData = (activeApps, inactiveApps) => {
  let tasks = {};

  let columns = JSON.parse(JSON.stringify(initialData.columns));

  (activeApps || []).forEach((app) => {
    const taskId = app.id;
    tasks[taskId] = {
      id: taskId,
      ...app,
    };
    if (app.status === boardKeys.awaitingPayment) {
      columns[boardKeys.awaitingPayment].taskIds.push(taskId);
    } else if (app.status === boardKeys.readyForDevelopment) {
      columns[boardKeys.readyForDevelopment].taskIds.push(taskId);
    } else if (app.status === boardKeys.inProgress) {
      columns[boardKeys.inProgress].taskIds.push(taskId);
    } else if (app.status === boardKeys.blocked) {
      columns[boardKeys.blocked].taskIds.push(taskId);
    }
  });

  (inactiveApps || []).forEach((app) => {
    const taskId = app.id;
    tasks[taskId] = {
      id: taskId,
      ...app,
    };
    columns[boardKeys.done].taskIds.push(taskId);
  });

  return {
    tasks,
    columns,
    columnOrder: initialData.columnOrder,
  };
};

const ListApplications = () => {
  const dispatch = useDispatch();
  const applicationsStore = useSelector((state) => state.applications);
  const [data, setData] = useState(initialData);

  useEffect(() => {
    const didLoad = async () => {
      const [active, inactive] = await Promise.all([
        dispatch(fetchActiveApplications()),
        dispatch(fetchInactiveApplications({ limit: PAGE_LIMIT })),
      ]);
      // Convert API response into board format
      const newData = transformApplicationsToBoardData(
        active.payload.data,
        inactive.payload.data
      );

      // Set the transformed data into local state
      setData(newData);
    };

    didLoad();
  }, [dispatch]);

  const loadMoreContent = async (e) => {
    e.preventDefault();
    const lastDocId = applicationsStore.inactive.lastDocId;

    const response = await dispatch(
      fetchInactiveApplications({
        lastDocId,
        limit: PAGE_LIMIT,
      })
    );

    if (fetchInactiveApplications.fulfilled.match(response)) {
      const newTasksData = response.payload.data;

      setData((prevData) => {
        // Extract only new tasks and taskIds
        let newTasks = {};
        let newTaskIds = [];

        newTasksData.forEach((app) => {
          const taskId = app.id;
          newTasks[taskId] = {
            id: taskId,
            ...app,
          };
          newTaskIds.push(taskId);
        });

        return {
          tasks: { ...prevData.tasks, ...newTasks },
          columns: {
            ...prevData.columns,
            [boardKeys.done]: {
              ...prevData.columns[boardKeys.done],
              taskIds: [
                ...prevData.columns[boardKeys.done].taskIds,
                ...newTaskIds,
              ],
            },
          },
          columnOrder: prevData.columnOrder,
        };
      });
    }
  };

  const onDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;

    const start = data.columns[source.droppableId];
    const finish = data.columns[destination.droppableId];

    if (start === finish) {
      const newTaskIds = Array.from(start.taskIds);
      newTaskIds.splice(source.index, 1);
      newTaskIds.splice(destination.index, 0, draggableId);

      const newColumn = { ...start, taskIds: newTaskIds };
      setData({
        ...data,
        columns: { ...data.columns, [newColumn.id]: newColumn },
      });
      return;
    }

    // Moving to a different column
    const startTaskIds = Array.from(start.taskIds);
    startTaskIds.splice(source.index, 1);
    const newStart = { ...start, taskIds: startTaskIds };

    const finishTaskIds = Array.from(finish.taskIds);
    finishTaskIds.splice(destination.index, 0, draggableId);
    const newFinish = { ...finish, taskIds: finishTaskIds };

    dispatch(
      updateApplicationStatus({
        id: draggableId,
        status: destination.droppableId,
      })
    );

    setData({
      ...data,
      columns: {
        ...data.columns,
        [newStart.id]: newStart,
        [newFinish.id]: newFinish,
      },
    });
  };

  return (
    <div className="antialiased bg-gray-50 dark:bg-gray-900">
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
          <Breadcrumb />
          {!applicationsStore.pageLoading && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 overflow-y-scroll">
              {initialData.columnOrder.map((key) => (
                <h2 className="pl-4 text-lg text-gray-400 font-semibold p-2 bg-gray-100 dark:bg-gray-800 rounded-tl-lg rounded-tr-lg border-t border-l border-r dark:border-gray-700">
                  {initialData.columns[key].title}
                </h2>
              ))}
            </div>
          )}
        </div>
        {applicationsStore.pageLoading ? (
          // Skeleton Loader
          <div className="animate-pulse mt-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 overflow-y-scroll">
              {initialData.columnOrder.map((key) => (
                <div className="ph-4 text-lg text-gray-400 font-semibold p-2 bg-gray-100 dark:bg-gray-800 rounded-lg rounded-lg border dark:border-gray-700 h-[400px]">
                  <div className="w-full h-32 bg-white" />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 overflow-y-scroll">
              {data.columnOrder.map((columnId) => {
                const column = data.columns[columnId];
                return (
                  <Droppable
                    key={column.id}
                    droppableId={column.id}
                    isDropDisabled={columnId === boardKeys.awaitingPayment}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`p-4 rounded-bl-lg rounded-br-lg border-b border-l border-r min-h-[300px] dark:border-gray-700 transition ${
                          snapshot.isDraggingOver
                            ? "bg-blue-200"
                            : "bg-gray-100 dark:bg-gray-800"
                        }`}
                      >
                        <div className="space-y-2">
                          {column.taskIds.map((taskId, index) => {
                            const task = data.tasks[taskId];
                            if (!task) return null;
                            return (
                              <Draggable
                                key={task && task.id}
                                draggableId={task && task.id}
                                index={index}
                                isDragDisabled={
                                  columnId === boardKeys.awaitingPayment ||
                                  (columnId === boardKeys.readyForDevelopment &&
                                    !task.assignee)
                                }
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className={`p-4 cursor-pointer rounded-md shadow-md transition-all ${
                                      snapshot.isDragging
                                        ? "bg-white text-white"
                                        : columnId === boardKeys.done &&
                                          task.status === "closed"
                                        ? "bg-red-100 text-black"
                                        : columnId === boardKeys.done &&
                                          task.status === "done"
                                        ? "bg-green-100 text-black"
                                        : "bg-white text-black"
                                    }`}
                                  >
                                    <Link
                                      to={`/applications/${task && task.id}`}
                                    >
                                      {task && (
                                        <>
                                          <div className="flex justify-between items-center">
                                            <div className="flex space-x-2">
                                              {/* Profile Image */}
                                              <Avatar
                                                alt="User profile image"
                                                img={
                                                  task.user &&
                                                  task.user.profileImage
                                                }
                                                rounded
                                                size="sm"
                                              />
                                              <div>
                                                <p className="text-sm font-semibold text-black">
                                                  {(task.user &&
                                                    task.user.fullName) ||
                                                    "Unknown User"}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                  {timestampToDateString(
                                                    task.createdAt
                                                  )}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                          {/* Service */}
                                          <p className="text-xs text-gray-500 mt-1">
                                            Service:{" "}
                                            <strong>
                                              {task.service.title || "Unknown"}
                                            </strong>
                                          </p>
                                          {/* Assignee */}
                                          <p className="text-xs text-gray-500 mt-1">
                                            Assignee:{" "}
                                            <strong
                                              className={
                                                !task.assignee && "text-red-500"
                                              }
                                            >
                                              {(task.assignee &&
                                                task.assignee.fullName) ||
                                                "Unassigned"}
                                            </strong>
                                          </p>
                                        </>
                                      )}
                                    </Link>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                          {/* Load More Button for "Done" Column */}
                          {column.id === boardKeys.done && (
                            <div className="flex justify-center mt-4">
                              {applicationsStore.inactive.hasMoreContent ===
                                true && (
                                <Button
                                  size="sm"
                                  color="gray"
                                  onClick={loadMoreContent}
                                  disabled={applicationsStore.moreLoading}
                                  isProcessing={applicationsStore.moreLoading}
                                  processingSpinner={
                                    <AiOutlineLoading className="h-6 w-6 animate-spin" />
                                  }
                                >
                                  Load More
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </Droppable>
                );
              })}
            </div>
          </DragDropContext>
        )}
      </main>
    </div>
  );
};

export default ListApplications;
